import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Form, Button, Card, Row, Col, Spinner, Modal, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { ThemeContext } from '../../context/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const generateId = () => Math.random().toString(36).substring(2, 9);

const RoutePlanner = () => {
    const { t } = useTranslation();
    const { isDark } = useContext(ThemeContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const queryRouteId = searchParams.get('route_id');
    const queryClientId = searchParams.get('client_id');

    // Helper to clear reorganization visual effect and clean URL query params
    const clearReorganizationMode = useCallback(() => {
        setTargetClientId('');
        if (searchParams.has('client_id')) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('client_id');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);
    
    // Master data
    const [routes, setRoutes] = useState([]);
    const [zones, setZones] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [merchandisers, setMerchandisers] = useState([]);
    const [routeTypeFilter, setRouteTypeFilter] = useState('all'); // 'all' | 'preventa' | 'merchandising'
    
    // Selection and planner state
    const [selectedRouteId, setSelectedRouteId] = useState('');
    const [targetClientId, setTargetClientId] = useState(queryClientId || '');
    const [activeRoute, setActiveRoute] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [searchCustomer, setSearchCustomer] = useState('');
    const [filterZone, setFilterZone] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);

    // Modal Create/Edit Route State
    const [showModal, setShowModal] = useState(false);
    const [savingRoute, setSavingRoute] = useState(false);
    const [showChangeVendor, setShowChangeVendor] = useState(false);
    const [sellerStatusFilter, setSellerStatusFilter] = useState('all'); // 'all' | 'free' | 'with_zone' | 'with_route'
    const [routeForm, setRouteForm] = useState({
        id: null,
        nombre_ruta: '',
        tipo_ruta: 'preventa',
        vendedor_id: '',
        zone_ids: [],
        is_active: true,
        is_multizona: false,
    });

    const daysOfWeek = [
        { id: '1', name: t('day_monday', 'Lunes') },
        { id: '2', name: t('day_tuesday', 'Martes') },
        { id: '3', name: t('day_wednesday', 'Miércoles') },
        { id: '4', name: t('day_thursday', 'Jueves') },
        { id: '5', name: t('day_friday', 'Viernes') },
        { id: '6', name: t('day_saturday', 'Sábado') },
        { id: '7', name: t('day_sunday', 'Domingo') }
    ];

    // Scheduled customers: { "1": [{id, customer_id}], "2": [...] }
    const [schedules, setSchedules] = useState({
        '1': [], '2': [], '3': [], '4': [], '5': [], '6': [], '7': []
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (queryRouteId && routes.length > 0 && selectedRouteId !== queryRouteId) {
            loadPlanner(queryRouteId);
        }
    }, [queryRouteId, routes, selectedRouteId]);

    useEffect(() => {
        if (queryClientId) {
            setTargetClientId(queryClientId);
        }
    }, [queryClientId]);

    const fetchInitialData = async () => {
        try {
            const [routesRes, zonesRes, sellersRes, merchandisersRes] = await Promise.all([
                axiosInstance.get('/admin/routes').catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/admin/zones').catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/admin/sellers').catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/admin/merchandisers').catch(() => ({ data: { data: [] } }))
            ]);

            const routesList = Array.isArray(routesRes.data?.data) ? routesRes.data.data : [];
            const zonesList = Array.isArray(zonesRes.data?.data) ? zonesRes.data.data : [];
            let sellersList = Array.isArray(sellersRes.data?.data) ? sellersRes.data.data : [];
            let merchandisersList = Array.isArray(merchandisersRes.data?.data) ? merchandisersRes.data.data : [];

            if (sellersList.length === 0) {
                const usersRes = await axiosInstance.get('/admin/users?rol=vendedor&per_page=100').catch(() => ({ data: { data: [] } }));
                const list = usersRes.data?.data?.data || usersRes.data?.data || [];
                if (Array.isArray(list)) sellersList = list;
            }

            if (merchandisersList.length === 0) {
                const usersRes = await axiosInstance.get('/admin/users?rol=mercaderista&per_page=100').catch(() => ({ data: { data: [] } }));
                const list = usersRes.data?.data?.data || usersRes.data?.data || [];
                if (Array.isArray(list) && list.length > 0) {
                    merchandisersList = list;
                } else {
                    const usersResCap = await axiosInstance.get('/admin/users?rol=Mercaderista&per_page=100').catch(() => ({ data: { data: [] } }));
                    const listCap = usersResCap.data?.data?.data || usersResCap.data?.data || [];
                    if (Array.isArray(listCap)) merchandisersList = listCap;
                }
            }

            setRoutes(routesList);
            setZones(zonesList);
            setSellers(sellersList);
            setMerchandisers(merchandisersList);
        } catch (error) {
            toast.error('Error al cargar datos de rutas y zonas');
            setRoutes([]);
            setZones([]);
            setSellers([]);
            setMerchandisers([]);
        }
    };

    const loadPlanner = async (routeId) => {
        setSelectedRouteId(routeId);
        if (!routeId) {
            setActiveRoute(null);
            setCustomers([]);
            setSchedules({'1': [], '2': [], '3': [], '4': [], '5': [], '6': [], '7': []});
            return;
        }

        setLoading(true);
        try {
            const res = await axiosInstance.get(`/admin/routes/planner/${routeId}`);
            const plannerData = res.data.data;
            
            setActiveRoute(plannerData.route);
            setCustomers(plannerData.customers || []);
            
            // Format schedules
            const rawSchedules = plannerData.schedules || {};
            const newSchedules = {};
            for (let i = 1; i <= 7; i++) {
                newSchedules[i.toString()] = rawSchedules[i.toString()] || [];
            }
            setSchedules(newSchedules);
        } catch (error) {
            toast.error('Error al cargar la planificación de la ruta');
        } finally {
            setLoading(false);
        }
    };

    const getCustomerInfo = (customerId) => {
        return customers.find(c => c.id === customerId) || { razon_social: 'Cliente', direccion_fisica: '', zone_code: '' };
    };

    const filteredRoutes = useMemo(() => {
        if (routeTypeFilter === 'all') return routes;
        return routes.filter(r => (r.tipo_ruta || 'preventa') === routeTypeFilter);
    }, [routes, routeTypeFilter]);

    const handleFilterChange = (type) => {
        setRouteTypeFilter(type);
        if (activeRoute && type !== 'all') {
            const currentType = activeRoute.tipo_ruta || 'preventa';
            if (currentType !== type) {
                loadPlanner('');
            }
        }
    };

    const filteredCustomers = useMemo(() => {
        return customers.filter(c => {
            const matchSearch = searchCustomer === '' || 
                (c.razon_social && c.razon_social.toLowerCase().includes(searchCustomer.toLowerCase())) ||
                (c.rif_cedula && c.rif_cedula.toLowerCase().includes(searchCustomer.toLowerCase()));
            const matchZone = filterZone === '' || c.zone_id === filterZone;
            return matchSearch && matchZone;
        });
    }, [customers, searchCustomer, filterZone]);

    const onDragEnd = (result) => {
        const { source, destination } = result;

        if (!destination) {
            if (source.droppableId !== 'master') {
                const newSchedules = { ...schedules };
                newSchedules[source.droppableId].splice(source.index, 1);
                setSchedules(newSchedules);
            }
            return;
        }

        if (destination.droppableId === 'master' && source.droppableId !== 'master') {
            const newSchedules = { ...schedules };
            newSchedules[source.droppableId].splice(source.index, 1);
            setSchedules(newSchedules);
            return;
        }

        if (source.droppableId === destination.droppableId && source.droppableId !== 'master') {
            const newSchedules = { ...schedules };
            const list = Array.from(newSchedules[source.droppableId]);
            const [removed] = list.splice(source.index, 1);
            list.splice(destination.index, 0, removed);
            newSchedules[source.droppableId] = list;
            setSchedules(newSchedules);
            return;
        }

        if (source.droppableId === 'master' && destination.droppableId !== 'master') {
            const draggedCustomerId = result.draggableId.replace('master-', '');
            
            const alreadyInDay = schedules[destination.droppableId].some(item => item.customer_id === draggedCustomerId);
            if (alreadyInDay) {
                toast.warning('Este cliente ya está agendado para este día.');
                return;
            }

            const newEntry = {
                id: generateId(),
                customer_id: draggedCustomerId
            };
            
            const newSchedules = { ...schedules };
            newSchedules[destination.droppableId].splice(destination.index, 0, newEntry);
            setSchedules(newSchedules);
            return;
        }

        if (source.droppableId !== 'master' && destination.droppableId !== 'master') {
            const newSchedules = { ...schedules };
            const sourceList = Array.from(newSchedules[source.droppableId]);
            const destList = Array.from(newSchedules[destination.droppableId]);
            
            const [removed] = sourceList.splice(source.index, 1);

            if (source.droppableId !== destination.droppableId) {
                const alreadyInDest = destList.some(item => item.customer_id === removed.customer_id);
                if (alreadyInDest) {
                    toast.warning('Este cliente ya está agendado para este día.');
                    sourceList.splice(source.index, 0, removed);
                    return;
                }
            }

            destList.splice(destination.index, 0, removed);
            newSchedules[source.droppableId] = sourceList;
            newSchedules[destination.droppableId] = destList;
            setSchedules(newSchedules);
        }
    };

    // Container reference and scroll handling for days carril
    const daysContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleDragStart = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleDragEnd = useCallback((result) => {
        setIsDragging(false);
        onDragEnd(result);
    }, [onDragEnd]);

    const handleDaysWheel = (e) => {
        if (!daysContainerRef.current) return;
        // If user rolls mouse wheel vertically, convert to horizontal scroll
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
            daysContainerRef.current.scrollLeft += e.deltaY;
        }
    };

    const handleSaveSchedule = async () => {
        if (!selectedRouteId) return;
        setSaving(true);
        try {
            const payloadSchedules = {};
            for (let i = 1; i <= 7; i++) {
                payloadSchedules[i.toString()] = schedules[i.toString()].map(item => item.customer_id);
            }

            await axiosInstance.post(`/admin/routes/planner/${selectedRouteId}/sync`, {
                schedules: payloadSchedules
            });
            toast.success(t('schedule_saved_success', 'Borrador de agenda semanal guardado exitosamente'));
        } catch (error) {
            toast.error(t('schedule_save_error', 'Error al guardar agenda'));
        } finally {
            setSaving(false);
        }
    };

    const totalScheduledVisits = useMemo(() => {
        return Object.values(schedules).reduce((acc, curr) => acc + (curr?.length || 0), 0);
    }, [schedules]);

    const handlePublishSchedule = async () => {
        if (!selectedRouteId) return;
        setPublishing(true);
        try {
            const payloadSchedules = {};
            for (let i = 1; i <= 7; i++) {
                payloadSchedules[i.toString()] = schedules[i.toString()].map(item => item.customer_id);
            }

            const res = await axiosInstance.post(`/admin/routes/planner/${selectedRouteId}/publish`, {
                schedules: payloadSchedules
            });

            if (res.data?.success) {
                toast.success(t('schedule_published_success', '¡Agenda semanal publicada y sincronizada exitosamente con la app móvil!'));
                setShowPublishModal(false);
                clearReorganizationMode();
                setActiveRoute(prev => ({
                    ...prev,
                    last_published_at: res.data.data?.last_published_at || new Date().toISOString(),
                    status: 'publicada'
                }));
            }
        } catch (error) {
            const msg = error.response?.data?.message || t('schedule_publish_error', 'Error al publicar agenda semanal');
            toast.error(msg);
        } finally {
            setPublishing(false);
        }
    };

    const selectedZone = useMemo(() => {
        if (routeForm.zone_ids.length === 1) {
            return zones.find(z => z.id === routeForm.zone_ids[0]) || null;
        }
        return null;
    }, [zones, routeForm.zone_ids]);

    const titularInfo = useMemo(() => {
        if (!selectedZone) return { titularUser: null, titularId: null };
        const titularUser = routeForm.tipo_ruta === 'merchandising' 
            ? selectedZone.mercaderista 
            : selectedZone.vendedor;
        const titularId = routeForm.tipo_ruta === 'merchandising'
            ? selectedZone.mercaderista_id
            : selectedZone.vendedor_id;
        return { titularUser, titularId };
    }, [selectedZone, routeForm.tipo_ruta]);

    const isDifferentFromTitular = useMemo(() => {
        if (routeForm.is_multizona || !selectedZone || !titularInfo.titularId || !routeForm.vendedor_id) {
            return false;
        }
        return String(routeForm.vendedor_id) !== String(titularInfo.titularId);
    }, [routeForm.is_multizona, selectedZone, titularInfo, routeForm.vendedor_id]);

    const isCurrentTitularAssigned = useMemo(() => {
        if (routeForm.is_multizona || !selectedZone || !titularInfo.titularId || !routeForm.vendedor_id) {
            return false;
        }
        return String(routeForm.vendedor_id) === String(titularInfo.titularId);
    }, [routeForm.is_multizona, selectedZone, titularInfo, routeForm.vendedor_id]);

    const hasNoTitular = useMemo(() => {
        if (routeForm.is_multizona || !selectedZone) return false;
        return !titularInfo.titularId;
    }, [routeForm.is_multizona, selectedZone, titularInfo]);

    const assignedUser = useMemo(() => {
        if (!routeForm.vendedor_id) return null;
        if (routeForm.tipo_ruta === 'merchandising') {
            return (merchandisers || []).find(m => String(m.id) === String(routeForm.vendedor_id)) || null;
        }
        return (sellers || []).find(s => String(s.id) === String(routeForm.vendedor_id)) || null;
    }, [routeForm.vendedor_id, routeForm.tipo_ruta, sellers, merchandisers]);

    const enrichedSellers = useMemo(() => {
        const list = routeForm.tipo_ruta === 'merchandising' ? (merchandisers || []) : (sellers || []);
        const isMerch = routeForm.tipo_ruta === 'merchandising';

        return list.map(user => {
            const assignedZone = (zones || []).find(z => 
                (isMerch ? String(z.mercaderista_id) : String(z.vendedor_id)) === String(user.id)
            );

            const assignedRoutes = (routes || []).filter(r => 
                (String(r.vendedor_id) === String(user.id) || String(r.responsable?.id) === String(user.id)) &&
                (!r.tipo_ruta || r.tipo_ruta === (isMerch ? 'merchandising' : 'preventa'))
            );

            const hasZone = !!assignedZone;
            const hasRoute = assignedRoutes.length > 0;
            const isFree = !hasZone && !hasRoute;
            const isTitularOfCurrentZone = titularInfo.titularId && String(user.id) === String(titularInfo.titularId);

            return {
                ...user,
                hasZone,
                hasRoute,
                isFree,
                isTitularOfCurrentZone,
                zoneCode: assignedZone?.code || null,
                zoneName: assignedZone?.name || null,
                routesCount: assignedRoutes.length,
                routeName: assignedRoutes.map(r => r.nombre_ruta).join(', ') || null,
            };
        });
    }, [routeForm.tipo_ruta, sellers, merchandisers, zones, routes, titularInfo.titularId]);

    const filteredEnrichedSellers = useMemo(() => {
        return enrichedSellers.filter(u => {
            if (sellerStatusFilter === 'free' && !u.isFree) return false;
            if (sellerStatusFilter === 'with_zone' && !u.hasZone) return false;
            if (sellerStatusFilter === 'with_route' && !u.hasRoute) return false;
            return true;
        });
    }, [enrichedSellers, sellerStatusFilter]);

    const sellerCounts = useMemo(() => {
        return {
            all: enrichedSellers.length,
            free: enrichedSellers.filter(s => s.isFree).length,
            with_zone: enrichedSellers.filter(s => s.hasZone).length,
            with_route: enrichedSellers.filter(s => s.hasRoute).length,
        };
    }, [enrichedSellers]);

    const assignedUserStatus = useMemo(() => {
        if (!routeForm.vendedor_id) return null;
        return enrichedSellers.find(s => String(s.id) === String(routeForm.vendedor_id)) || null;
    }, [routeForm.vendedor_id, enrichedSellers]);

    const openCreateModal = () => {
        const defaultType = routeTypeFilter === 'merchandising' ? 'merchandising' : 'preventa';
        setRouteForm({
            id: null,
            nombre_ruta: '',
            tipo_ruta: defaultType,
            vendedor_id: '',
            zone_ids: [],
            is_active: true,
            is_multizona: false
        });
        setShowChangeVendor(false);
        setSellerStatusFilter('all');
        setShowModal(true);
    };

    const openEditModal = () => {
        if (!activeRoute) return;
        const currentZones = (activeRoute.zones || []).map(z => z.id);
        const isMulti = currentZones.length > 1;
        const currentVendedorId = (activeRoute.responsable || activeRoute.vendedor)?.id || '';
        setRouteForm({
            id: activeRoute.id,
            nombre_ruta: activeRoute.nombre_ruta,
            tipo_ruta: activeRoute.tipo_ruta || 'preventa',
            vendedor_id: currentVendedorId,
            zone_ids: currentZones,
            is_active: activeRoute.is_active ?? true,
            is_multizona: isMulti
        });
        setShowChangeVendor(false);
        setSellerStatusFilter('all');
        setShowModal(true);
    };

    const handleSelectZoneSingle = (zone) => {
        setRouteForm(prev => {
            const newZoneIds = [zone.id];
            const titularId = prev.tipo_ruta === 'merchandising' ? zone.mercaderista_id : zone.vendedor_id;
            const newVendedorId = titularId || prev.vendedor_id || '';

            let newNombreRuta = prev.nombre_ruta;
            const typeLabel = prev.tipo_ruta === 'merchandising' ? 'Mercadeo' : 'Preventa';
            const isDefaultPattern = !prev.nombre_ruta.trim() || 
                prev.nombre_ruta.startsWith('Ruta Preventa - ') || 
                prev.nombre_ruta.startsWith('Ruta Mercadeo - ');

            if (!prev.id && isDefaultPattern) {
                newNombreRuta = `Ruta ${typeLabel} - ${zone.name}`;
            }

            return {
                ...prev,
                zone_ids: newZoneIds,
                vendedor_id: newVendedorId,
                nombre_ruta: newNombreRuta
            };
        });
    };

    const toggleZoneMulti = (zoneId) => {
        setRouteForm(prev => {
            const current = [...prev.zone_ids];
            const idx = current.indexOf(zoneId);
            if (idx > -1) {
                current.splice(idx, 1);
            } else {
                current.push(zoneId);
            }

            let newVendedorId = prev.vendedor_id;
            if (current.length === 1 && !newVendedorId) {
                const z = zones.find(item => item.id === zoneId);
                if (z) {
                    const titularId = prev.tipo_ruta === 'merchandising' ? z.mercaderista_id : z.vendedor_id;
                    if (titularId) newVendedorId = titularId;
                }
            }

            return {
                ...prev,
                zone_ids: current,
                vendedor_id: newVendedorId
            };
        });
    };

    const handleToggleMultiZone = (enabled) => {
        setRouteForm(prev => {
            if (!enabled && prev.zone_ids.length > 1) {
                const firstZoneId = prev.zone_ids[0];
                const firstZone = zones.find(z => z.id === firstZoneId);
                const titularId = firstZone 
                    ? (prev.tipo_ruta === 'merchandising' ? firstZone.mercaderista_id : firstZone.vendedor_id)
                    : null;
                return {
                    ...prev,
                    is_multizona: false,
                    zone_ids: [firstZoneId],
                    vendedor_id: titularId || prev.vendedor_id
                };
            }
            return {
                ...prev,
                is_multizona: enabled
            };
        });
    };

    const handleRouteTypeChange = (newType) => {
        setRouteForm(prev => {
            let newVendedorId = '';
            if (prev.zone_ids.length === 1) {
                const z = zones.find(item => item.id === prev.zone_ids[0]);
                if (z) {
                    newVendedorId = (newType === 'merchandising' ? z.mercaderista_id : z.vendedor_id) || '';
                }
            } else {
                newVendedorId = (newType === 'merchandising' ? merchandisers[0]?.id : sellers[0]?.id) || '';
            }

            let newNombre = prev.nombre_ruta;
            if (!prev.id && prev.zone_ids.length === 1) {
                const z = zones.find(item => item.id === prev.zone_ids[0]);
                if (z) {
                    newNombre = `Ruta ${newType === 'merchandising' ? 'Mercadeo' : 'Preventa'} - ${z.name}`;
                }
            }

            return {
                ...prev,
                tipo_ruta: newType,
                vendedor_id: newVendedorId,
                nombre_ruta: newNombre
            };
        });
    };

    const handleSaveRoute = async (e) => {
        e.preventDefault();
        if (!routeForm.nombre_ruta.trim()) {
            toast.warning('Ingresa un nombre para la ruta');
            return;
        }
        if (!routeForm.vendedor_id) {
            toast.warning(routeForm.tipo_ruta === 'merchandising' ? 'Selecciona un mercaderista asignado' : 'Selecciona un vendedor asignado');
            return;
        }
        if (routeForm.zone_ids.length === 0) {
            toast.warning('Selecciona al menos una zona geográfica');
            return;
        }
        if (!routeForm.is_multizona && routeForm.zone_ids.length > 1) {
            toast.warning('Una ruta estándar solo puede tener 1 zona territorial. Activa el modo Multi-Zona para rutas de contingencia.');
            return;
        }

        setSavingRoute(true);
        try {
            const payload = {
                nombre_ruta: routeForm.nombre_ruta.trim(),
                tipo_ruta: routeForm.tipo_ruta,
                vendedor_id: routeForm.vendedor_id,
                zone_ids: routeForm.zone_ids,
                is_active: routeForm.is_active
            };

            if (routeForm.id) {
                // Update
                const res = await axiosInstance.put(`/admin/routes/${routeForm.id}`, payload);
                toast.success('Ruta comercial actualizada exitosamente');
                setShowModal(false);
                await fetchInitialData();
                loadPlanner(routeForm.id);
            } else {
                // Create
                const res = await axiosInstance.post('/admin/routes', payload);
                toast.success('Ruta comercial creada exitosamente');
                setShowModal(false);
                await fetchInitialData();
                if (res.data?.data?.id) {
                    loadPlanner(res.data.data.id);
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al guardar la ruta comercial');
        } finally {
            setSavingRoute(false);
        }
    };

    return (
        <div className="container-fluid p-0">
            <ToastContainer />
            
            {/* Quick Navigation Stepper / Flow Breadcrumbs */}
            <div 
                className="d-flex flex-wrap align-items-center justify-content-between mb-4 p-2 px-3 rounded shadow-sm" 
                style={{ 
                    backgroundColor: isDark ? '#161e38' : '#ffffff', 
                    border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' 
                }}
            >
                <div className="d-flex align-items-center gap-2 small flex-wrap">
                    <span className="text-muted fw-bold me-1 text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                        Flujo Logístico:
                    </span>
                    <Link 
                        to="/general-pages/zones-manager" 
                        className="text-decoration-none d-inline-flex align-items-center gap-1 px-2 py-1 rounded" 
                        style={{ 
                            color: isDark ? '#93c5fd' : '#183eeb', 
                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                            fontWeight: '600',
                            fontSize: '0.78rem'
                        }}
                    >
                        <i className="mdi mdi-map-legend"></i> 1. Zonas y Territorios
                    </Link>
                    <i className="mdi mdi-chevron-right text-muted"></i>
                    <span 
                        className="fw-bold d-inline-flex align-items-center gap-1 px-2 py-1 rounded shadow-sm" 
                        style={{ 
                            color: '#ffffff', 
                            backgroundColor: '#183eeb',
                            fontSize: '0.78rem'
                        }}
                    >
                        <i className="mdi mdi-calendar-clock"></i> 2. Planificador Semanal
                    </span>
                    <i className="mdi mdi-chevron-right text-muted"></i>
                    <Link 
                        to="/general-pages/agenda-monitor" 
                        className="text-decoration-none d-inline-flex align-items-center gap-1 px-2 py-1 rounded" 
                        style={{ 
                            color: isDark ? '#93c5fd' : '#183eeb', 
                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                            fontWeight: '600',
                            fontSize: '0.78rem'
                        }}
                    >
                        <i className="mdi mdi-monitor-dashboard"></i> 3. Monitoreo en Vivo
                    </Link>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                    <Link 
                        to="/general-pages/agenda-monitor" 
                        className="btn btn-sm" 
                        style={{ 
                            fontSize: '0.78rem',
                            fontWeight: '600',
                            borderRadius: '8px',
                            color: isDark ? '#93c5fd' : '#183eeb',
                            border: isDark ? '1px solid #3b82f6' : '1px solid #cbd5e1'
                        }}
                    >
                        <i className="mdi mdi-eye-outline me-1"></i> Ir a Monitoreo en Vivo
                    </Link>
                </div>
            </div>

            {/* Page Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                <div>
                    <h3 style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold', margin: 0 }}>
                        {t('route_planner_title', 'Planificador de Rutas (Agenda)')}
                    </h3>
                    <p className="text-muted small mb-0">
                        Diseño y organización semanal de visitas comerciales por vendedor y zonas.
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <Button 
                        className="btn-rutex-action"
                        style={{ 
                            borderRadius: '8px', 
                            fontWeight: '600',
                            backgroundColor: isDark ? 'rgba(24, 62, 235, 0.15)' : '#ffffff',
                            color: isDark ? '#60a5fa' : '#183eeb',
                            border: isDark ? '1.5px solid #3b82f6' : '1.5px solid #183eeb',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        onClick={openCreateModal}
                    >
                        <i className="mdi mdi-plus fs-6"></i> Nueva Ruta Comercial
                    </Button>
                    <Button 
                        variant="outline-secondary"
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                        disabled={!selectedRouteId || saving || publishing}
                        onClick={handleSaveSchedule}
                    >
                        {saving ? <Spinner size="sm" /> : <><i className="mdi mdi-content-save me-1"></i> Guardar Borrador</>}
                    </Button>
                    <Button 
                        style={{ 
                            backgroundColor: '#183eeb', 
                            border: 'none', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            color: '#ffffff',
                            boxShadow: '0 4px 12px rgba(24, 62, 235, 0.35)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        disabled={!selectedRouteId || saving || publishing || totalScheduledVisits === 0}
                        onClick={() => setShowPublishModal(true)}
                    >
                        <i className="mdi mdi-cloud-upload-outline fs-6"></i>
                        Publicar Agenda Semanal
                    </Button>
                </div>
            </div>

            {/* Route Selector & Info Card */}
            <Card 
                className="mb-4 shadow-sm border-0" 
                style={{ 
                    borderRadius: '15px', 
                    backgroundColor: isDark ? '#161e38' : '#ffffff',
                    border: isDark ? '1px solid #2a3759' : 'none'
                }}
            >
                <Card.Body className="p-4">
                    {/* Filter Tabs by Route Type */}
                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                        <span className="small fw-bold me-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            <i className="mdi mdi-filter-variant me-1"></i>Filtrar tipo:
                        </span>
                        <Button 
                            variant="sm"
                            size="sm"
                            style={{
                                borderRadius: '20px',
                                fontWeight: '600',
                                padding: '4px 12px',
                                fontSize: '0.8rem',
                                backgroundColor: routeTypeFilter === 'all' ? '#183eeb' : (isDark ? '#10172d' : '#f1f5f9'),
                                color: routeTypeFilter === 'all' ? '#ffffff' : (isDark ? '#cbd5e1' : '#475569'),
                                border: isDark ? '1px solid #233052' : '1px solid #e2e8f0'
                            }}
                            onClick={() => handleFilterChange('all')}
                        >
                            Todas ({routes.length})
                        </Button>
                        <Button 
                            variant="sm"
                            size="sm"
                            style={{
                                borderRadius: '20px',
                                fontWeight: '600',
                                padding: '4px 12px',
                                fontSize: '0.8rem',
                                backgroundColor: routeTypeFilter === 'preventa' ? '#183eeb' : (isDark ? '#10172d' : '#f1f5f9'),
                                color: routeTypeFilter === 'preventa' ? '#ffffff' : (isDark ? '#cbd5e1' : '#475569'),
                                border: isDark ? '1px solid #233052' : '1px solid #e2e8f0'
                            }}
                            onClick={() => handleFilterChange('preventa')}
                        >
                            <i className="mdi mdi-cart-outline me-1"></i>Rutas Preventa ({routes.filter(r => (r.tipo_ruta || 'preventa') === 'preventa').length})
                        </Button>
                        <Button 
                            variant="sm"
                            size="sm"
                            style={{
                                borderRadius: '20px',
                                fontWeight: '600',
                                padding: '4px 12px',
                                fontSize: '0.8rem',
                                backgroundColor: routeTypeFilter === 'merchandising' ? '#0ea5e9' : (isDark ? '#10172d' : '#f1f5f9'),
                                color: routeTypeFilter === 'merchandising' ? '#ffffff' : (isDark ? '#cbd5e1' : '#475569'),
                                border: isDark ? '1px solid #233052' : '1px solid #e2e8f0'
                            }}
                            onClick={() => handleFilterChange('merchandising')}
                        >
                            <i className="mdi mdi-store-search me-1"></i>Rutas Mercadeo ({routes.filter(r => r.tipo_ruta === 'merchandising').length})
                        </Button>
                    </div>

                    <Row className="align-items-center g-3">
                        <Col lg={5} md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 'bold', color: isDark ? '#f8fafc' : '#010028' }}>
                                    <i className="mdi mdi-map-marker-path text-primary me-1"></i> Selecciona la Ruta Comercial
                                </Form.Label>
                                <Form.Select 
                                    value={selectedRouteId} 
                                    onChange={(e) => loadPlanner(e.target.value)}
                                    className="customer-filter-control w-100 shadow-none cursor-pointer"
                                >
                                    <option value="">-- Elige una Ruta Comercial --</option>
                                    {(filteredRoutes || []).map(r => {
                                        const isMerch = r.tipo_ruta === 'merchandising';
                                        const userObj = r.responsable || r.vendedor;
                                        const isMulti = (r.zones || []).length > 1;
                                        return (
                                            <option key={r.id} value={r.id}>
                                                [{isMerch ? 'MERCADERISTA' : 'PREVENTA'}]{isMulti ? ' [MULTI-ZONA]' : ''} {r.nombre_ruta} {userObj ? `(${userObj.nombre_completo})` : ''}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        {activeRoute && (
                            <Col lg={7} md={6}>
                                <div 
                                    className="d-flex flex-wrap align-items-center justify-content-between p-3 rounded" 
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#f8f9fc', 
                                        border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' 
                                    }}
                                >
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <Badge 
                                                bg={activeRoute.tipo_ruta === 'merchandising' ? 'info' : 'primary'}
                                                style={{ fontSize: '0.72rem', fontWeight: '600', padding: '3px 7px' }}
                                            >
                                                <i className={activeRoute.tipo_ruta === 'merchandising' ? 'mdi mdi-store-search me-1' : 'mdi mdi-cart-outline me-1'}></i>
                                                {activeRoute.tipo_ruta === 'merchandising' ? 'Ruta de Mercaderista' : 'Ruta de Preventa'}
                                            </Badge>

                                            {activeRoute.zones && activeRoute.zones.length > 1 ? (
                                                <Badge 
                                                    bg="warning" 
                                                    text="dark" 
                                                    style={{ 
                                                        fontSize: '0.72rem', 
                                                        fontWeight: 'bold', 
                                                        padding: '3px 7px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <i className="mdi mdi-alert-octagon"></i>
                                                    Multi-Zona ({activeRoute.zones.length})
                                                </Badge>
                                            ) : (
                                                <Badge 
                                                    bg="secondary" 
                                                    style={{ 
                                                        fontSize: '0.72rem', 
                                                        fontWeight: '600', 
                                                        padding: '3px 7px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <i className="mdi mdi-map-marker-check"></i>
                                                    Zona Única
                                                </Badge>
                                            )}

                                            {activeRoute.last_published_at ? (
                                                <Badge 
                                                    bg="success" 
                                                    style={{ 
                                                        fontSize: '0.72rem', 
                                                        fontWeight: '600', 
                                                        padding: '3px 7px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <i className="mdi mdi-check-circle"></i>
                                                    Publicada ({new Date(activeRoute.last_published_at).toLocaleDateString([], { month: 'short', day: 'numeric' })})
                                                </Badge>
                                            ) : (
                                                <Badge 
                                                    bg="warning" 
                                                    text="dark" 
                                                    style={{ 
                                                        fontSize: '0.72rem', 
                                                        fontWeight: '600', 
                                                        padding: '3px 7px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <i className="mdi mdi-clock-alert-outline"></i>
                                                    Borrador (Sin Publicar)
                                                </Badge>
                                            )}

                                            <span className="fw-bold" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                                                {(activeRoute.responsable || activeRoute.vendedor)?.nombre_completo || 'Responsable no asignado'}
                                            </span>
                                            <small className={isDark ? "text-light opacity-75" : "text-muted"}>
                                                ({(activeRoute.responsable || activeRoute.vendedor)?.email || 'sin email'})
                                            </small>
                                        </div>
                                        <div className="d-flex flex-wrap align-items-center gap-1 mt-2">
                                            <span className="small me-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Zonas:</span>
                                            {(activeRoute.zones || []).map(z => (
                                                <Badge key={z.id} bg="secondary" style={{ fontSize: '0.75rem', fontWeight: '500' }}>
                                                    {z.code} - {z.name}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                    <Button 
                                        className="btn-rutex-config mt-2 mt-md-0"
                                        size="sm" 
                                        style={{
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                            color: isDark ? '#93c5fd' : '#1e293b',
                                            border: isDark ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        onClick={openEditModal}
                                        title="Modificar Zonas o Responsable de esta Ruta"
                                    >
                                        <i className="mdi mdi-pencil me-1"></i> Configurar
                                    </Button>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {/* Reorganization alert when redirected from Agenda Monitor */}
            {targetClientId && (
                <div 
                    className="alert d-flex align-items-center justify-content-between p-3 mb-3 border shadow-sm" 
                    style={{ 
                        borderRadius: '12px', 
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff', 
                        borderColor: isDark ? 'rgba(59, 130, 246, 0.4)' : '#bfdbfe',
                        color: isDark ? '#93c5fd' : '#1e40af'
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <i className="mdi mdi-map-marker-path fs-4 text-primary"></i>
                        <div>
                            <strong>Modo Reorganización desde Agenda de Visitas:</strong>
                            <div className="small mt-1" style={{ color: isDark ? '#cbd5e1' : '#3b82f6' }}>
                                Visualizando la ruta seleccionada para reorganizar la visita del cliente destacado. Arrastra la tarjeta al día conveniente y haz clic en <strong>Guardar Planificación</strong> para actualizar la agenda del vendedor.
                            </div>
                        </div>
                    </div>
                    <button 
                        type="button" 
                        className="btn-close ms-3" 
                        onClick={clearReorganizationMode} 
                        aria-label="Cerrar"
                    ></button>
                </div>
            )}

            {/* Planner Body */}
            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#183eeb' }} />
                    <p className="mt-3 text-muted">Cargando clientes y agenda de la ruta...</p>
                </div>
            ) : selectedRouteId ? (
                <DragDropContext 
                    onDragStart={handleDragStart} 
                    onDragEnd={handleDragEnd}
                    autoScrollerOptions={{
                        startFromPercentage: 0.25,
                        maxPixelScroll: 25,
                    }}
                >
                    <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
                        
                        {/* Master List Column (Fija a la izquierda) */}
                        <div style={{ width: '310px', minWidth: '310px', maxWidth: '310px', flexShrink: 0, position: 'sticky', top: '15px' }}>
                            <div 
                                style={{ 
                                    backgroundColor: isDark ? '#161e38' : '#f4f5f7', 
                                    padding: '16px', 
                                    borderRadius: '12px', 
                                    height: '100%', 
                                    border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' 
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 style={{ fontWeight: 'bold', color: isDark ? '#ffffff' : '#010028', margin: 0 }}>
                                        Clientes Activos ({filteredCustomers.length})
                                    </h6>
                                    <Badge bg="success">Solo Activos</Badge>
                                </div>
                                <p className="text-muted small mb-3" style={{ fontSize: '0.75rem' }}>
                                    Solo clientes con estatus activo pueden ser planificados en rutas.
                                </p>

                                {/* Customer Search & Zone Filter (Standard customer-filter-control) */}
                                <div className="mb-3">
                                    <Form.Control 
                                        placeholder="Buscar por RIF o Nombre..." 
                                        value={searchCustomer}
                                        onChange={(e) => setSearchCustomer(e.target.value)}
                                        className="customer-filter-control w-100 shadow-none mb-2"
                                        style={{ height: '38px', fontSize: '0.82rem' }}
                                    />

                                    {activeRoute?.zones?.length > 1 && (
                                        <Form.Select 
                                            value={filterZone}
                                            onChange={(e) => setFilterZone(e.target.value)}
                                            className="customer-filter-control w-100 shadow-none cursor-pointer"
                                            style={{ height: '38px', fontSize: '0.82rem' }}
                                        >
                                            <option value="">Todas las zonas ({activeRoute.zones.length})</option>
                                            {activeRoute.zones.map(z => (
                                                <option key={z.id} value={z.id}>{z.code} - {z.name}</option>
                                            ))}
                                        </Form.Select>
                                    )}
                                </div>
                                
                                <Droppable droppableId="master" isDropDisabled={true}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{ minHeight: '520px', maxHeight: '70vh', overflowY: 'auto', paddingRight: '4px' }}
                                        >
                                            {filteredCustomers.map((c, index) => {
                                                const isTarget = targetClientId && String(c.id) === String(targetClientId);
                                                return (
                                                    <Draggable key={`master-${c.id}`} draggableId={`master-${c.id}`} index={index}>
                                                        {(provided, snapshot) => (
                                                            <React.Fragment>
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={{
                                                                        userSelect: 'none',
                                                                        padding: '10px 12px',
                                                                        margin: '0 0 8px 0',
                                                                        backgroundColor: snapshot.isDragging 
                                                                            ? (isDark ? '#223055' : '#e6f0ff') 
                                                                            : isTarget
                                                                                ? (isDark ? 'rgba(245, 158, 11, 0.18)' : '#fffbeb')
                                                                                : (isDark ? '#1c2544' : '#ffffff'),
                                                                        borderRadius: '8px',
                                                                        boxShadow: isTarget ? '0 0 10px rgba(245, 158, 11, 0.45)' : '0 1px 3px rgba(0,0,0,0.12)',
                                                                        borderLeft: isTarget ? '4px solid #f59e0b' : '4px solid #183eeb',
                                                                        borderTop: isTarget ? '1px solid #f59e0b' : (isDark ? '1px solid #2d3c63' : '1px solid #e0e6ed'),
                                                                        borderRight: isTarget ? '1px solid #f59e0b' : (isDark ? '1px solid #2d3c63' : '1px solid #e0e6ed'),
                                                                        borderBottom: isTarget ? '1px solid #f59e0b' : (isDark ? '1px solid #2d3c63' : '1px solid #e0e6ed'),
                                                                        ...provided.draggableProps.style,
                                                                    }}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <div style={{ fontWeight: '600', fontSize: '0.85rem', color: isDark ? '#ffffff' : '#010028' }}>
                                                                            {c.razon_social}
                                                                            {isTarget && (
                                                                                <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.62rem' }}>
                                                                                    Reorganizar
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        {c.zone_code && (
                                                                            <Badge bg="primary" style={{ fontSize: '0.65rem' }}>
                                                                                {c.zone_code}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                <div 
                                                                    className="text-truncate" 
                                                                    style={{ 
                                                                        fontSize: '0.72rem', 
                                                                        marginTop: '2px',
                                                                        color: isDark ? '#94a3b8' : '#6c757d'
                                                                    }}
                                                                >
                                                                    {c.direccion_fisica || 'Sin dirección registrada'}
                                                                </div>
                                                            </div>
                                                            
                                                            {snapshot.isDragging && (
                                                                <div
                                                                    style={{
                                                                        padding: '10px 12px',
                                                                        margin: '0 0 8px 0',
                                                                        backgroundColor: isDark ? '#12182c' : '#f8f9fa',
                                                                        borderRadius: '8px',
                                                                        borderLeft: '4px solid #64748b',
                                                                        opacity: 0.4
                                                                    }}
                                                                >
                                                                    <div style={{ fontWeight: '600', fontSize: '0.85rem', color: isDark ? '#ffffff' : '#010028' }}>{c.razon_social}</div>
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    )}
                                                </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                            {filteredCustomers.length === 0 && (
                                                <div className="text-center py-4 text-muted small">
                                                    No se encontraron clientes para este filtro.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        </div>

                        {/* Carril de Días de la Semana (Desplazable con Auto-Scroll y Rueda) */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                <span className="small text-muted fw-bold" style={{ fontSize: '0.78rem' }}>
                                    <i className="mdi mdi-calendar-week me-1 text-primary"></i>
                                    Días de Visita (Lunes a Domingo) — Desliza con la rueda o arrastra
                                </span>
                                <div className="d-flex align-items-center gap-1">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', padding: 0 }}
                                        onClick={() => daysContainerRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
                                        title="Desplazar hacia la izquierda"
                                    >
                                        <i className="mdi mdi-chevron-left" style={{ fontSize: '1.1rem' }}></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', padding: 0 }}
                                        onClick={() => daysContainerRef.current?.scrollBy({ left: 320, behavior: 'smooth' })}
                                        title="Desplazar hacia la derecha"
                                    >
                                        <i className="mdi mdi-chevron-right" style={{ fontSize: '1.1rem' }}></i>
                                    </button>
                                </div>
                            </div>

                            <div 
                                ref={daysContainerRef}
                                onWheel={handleDaysWheel}
                                style={{ 
                                    display: 'flex', 
                                    overflowX: 'auto', 
                                    gap: '16px', 
                                    paddingBottom: '20px',
                                    scrollBehavior: isDragging ? 'auto' : 'smooth'
                                }}
                            >
                                {/* Days Columns (Lunes a Domingo) */}
                        {daysOfWeek.map(day => (
                            <div key={day.id} style={{ minWidth: '280px', maxWidth: '280px' }}>
                                <div 
                                    style={{ 
                                        backgroundColor: isDark ? '#161e38' : '#f0f4ff', 
                                        padding: '16px', 
                                        borderRadius: '12px', 
                                        height: '100%', 
                                        borderTop: '4px solid #183eeb', 
                                        border: isDark ? '1px solid #2a3759' : '1px solid #d0e0fc' 
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 style={{ fontWeight: 'bold', margin: 0, color: isDark ? '#60a5fa' : '#183eeb' }}>{day.name}</h6>
                                        <Badge bg="primary" pill style={{ fontSize: '0.78rem' }}>
                                            {schedules[day.id]?.length || 0} visitas
                                        </Badge>
                                    </div>
                                    
                                    <Droppable droppableId={day.id}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                style={{
                                                    minHeight: '520px',
                                                    backgroundColor: snapshot.isDraggingOver 
                                                        ? (isDark ? 'rgba(24, 62, 235, 0.25)' : '#e2eaff') 
                                                        : 'transparent',
                                                    borderRadius: '8px',
                                                    transition: 'background-color 0.2s ease',
                                                    paddingRight: '2px'
                                                }}
                                            >
                                                {schedules[day.id].map((item, index) => {
                                                    const customer = getCustomerInfo(item.customer_id);
                                                    const isTarget = targetClientId && String(item.customer_id) === String(targetClientId);
                                                    return (
                                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={{
                                                                        userSelect: 'none',
                                                                        padding: '10px 12px',
                                                                        margin: '0 0 8px 0',
                                                                        backgroundColor: snapshot.isDragging 
                                                                            ? (isDark ? '#223055' : '#e6f0ff')
                                                                            : isTarget
                                                                                ? (isDark ? 'rgba(59, 130, 246, 0.2)' : '#eff6ff')
                                                                                : (isDark ? '#1c2544' : '#ffffff'),
                                                                        borderRadius: '8px',
                                                                        boxShadow: snapshot.isDragging 
                                                                            ? '0 5px 15px rgba(0,0,0,0.3)' 
                                                                            : isTarget
                                                                                ? '0 0 10px rgba(59, 130, 246, 0.45)'
                                                                                : '0 1px 3px rgba(0,0,0,0.08)',
                                                                        border: isTarget
                                                                            ? (isDark ? '2px solid #3b82f6' : '2px solid #183eeb')
                                                                            : (isDark ? '1px solid #2d3c63' : '1px solid #e0e6ed'),
                                                                        position: 'relative',
                                                                        ...provided.draggableProps.style,
                                                                    }}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                                                        <div className="d-flex align-items-center gap-1">
                                                                            <Badge bg={isDark ? "secondary" : "dark"} style={{ fontSize: '0.65rem' }}>
                                                                                #{index + 1}
                                                                            </Badge>
                                                                            {isTarget && (
                                                                                <Badge bg="warning" text="dark" style={{ fontSize: '0.62rem' }}>
                                                                                    A reorganizar
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        {customer.zone_code && (
                                                                            <Badge bg="primary" style={{ fontSize: '0.62rem' }}>
                                                                                {customer.zone_code}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ fontWeight: '600', fontSize: '0.84rem', color: isDark ? '#ffffff' : '#010028' }}>
                                                                        {customer.razon_social}
                                                                    </div>
                                                                    <div 
                                                                        className="text-truncate" 
                                                                        style={{ 
                                                                            fontSize: '0.7rem',
                                                                            color: isDark ? '#94a3b8' : '#6c757d'
                                                                        }}
                                                                    >
                                                                        {customer.direccion_fisica || 'Sin dirección'}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                                {schedules[day.id].length === 0 && (
                                                    <div className="text-center py-5 small" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                                                        <i className="mdi mdi-cursor-move fs-4 d-block mb-1"></i>
                                                        Arrastra clientes aquí
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </DragDropContext>
            ) : (
                <Card 
                    className="text-center py-5 border-0 shadow-sm" 
                    style={{ 
                        borderRadius: '15px',
                        backgroundColor: isDark ? '#161e38' : '#ffffff',
                        color: isDark ? '#ffffff' : '#010028',
                        border: isDark ? '1px solid #2a3759' : 'none'
                    }}
                >
                    <Card.Body>
                        <i className="mdi mdi-map-marker-distance display-4 text-muted mb-3 d-block"></i>
                        <h5 style={{ color: isDark ? '#ffffff' : '#010028' }}>Selecciona o crea una Ruta Comercial</h5>
                        <p className="text-muted">Elige una ruta en el selector superior para ver sus clientes y organizar la agenda semanal.</p>
                    </Card.Body>
                </Card>
            )}

            {/* Modal Crear / Configurar Ruta Comercial (Ultra-Limpio y Ágil) */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" size="lg">
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#010028', color: '#ffffff' }}>
                    <Modal.Title style={{ fontWeight: 'bold', fontSize: '1.15rem' }}>
                        <i className="mdi mdi-map-marker-path text-primary me-2"></i>
                        {routeForm.id ? 'Configurar Ruta Comercial' : 'Crear Nueva Ruta Comercial'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveRoute}>
                    <Modal.Body className="p-3" style={{ backgroundColor: isDark ? '#161e38' : '#f8fafd', color: isDark ? '#f8fafc' : '#010028' }}>
                        <Row className="g-3">
                            {/* COLUMNA IZQUIERDA: Territorio y Cobertura */}
                            <Col lg={7}>
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.92rem' }}>
                                        <i className="mdi mdi-map-legend text-primary me-1"></i> Seleccionar Territorio
                                    </h6>
                                    <div className="btn-group p-1 rounded border" style={{ backgroundColor: isDark ? '#10172d' : '#f1f5f9', borderColor: isDark ? '#2a3759' : '#e2e8f0' }}>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${!routeForm.is_multizona ? 'btn-primary' : 'btn-link text-muted'} fw-semibold px-2 py-1`}
                                            style={{ borderRadius: '6px', fontSize: '0.74rem', textDecoration: 'none' }}
                                            onClick={() => handleToggleMultiZone(false)}
                                        >
                                            <i className="mdi mdi-map-marker-check me-1"></i> Regular (1 Zona)
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${routeForm.is_multizona ? 'btn-warning text-dark' : 'btn-link text-muted'} fw-semibold px-2 py-1`}
                                            style={{ borderRadius: '6px', fontSize: '0.74rem', textDecoration: 'none' }}
                                            onClick={() => handleToggleMultiZone(true)}
                                        >
                                            <i className="mdi mdi-alert-octagon me-1"></i> Multi-Zona
                                        </button>
                                    </div>
                                </div>

                                {/* Cuadrícula interactiva de zonas */}
                                <div 
                                    className="pe-1"
                                    style={{ 
                                        maxHeight: '380px', 
                                        overflowY: 'auto', 
                                        paddingRight: '4px' 
                                    }}
                                >
                                    <Row className="g-2">
                                        {(zones || []).map(z => {
                                            const isSelected = routeForm.zone_ids.includes(z.id);
                                            const titularUserObj = routeForm.tipo_ruta === 'merchandising' ? z.mercaderista : z.vendedor;
                                            const titularName = titularUserObj?.nombre_completo || null;
                                            
                                            return (
                                                <Col md={6} key={z.id}>
                                                    <div 
                                                        className="p-2 px-3 rounded border d-flex align-items-center justify-content-between cursor-pointer h-100"
                                                        style={{ 
                                                            cursor: 'pointer',
                                                            backgroundColor: isDark 
                                                                ? (isSelected ? '#1e2d52' : '#10172d') 
                                                                : (isSelected ? '#eff6ff' : '#ffffff'),
                                                            borderColor: isDark 
                                                                ? (isSelected ? '#3b82f6' : '#2a3759') 
                                                                : (isSelected ? '#183eeb' : '#dee2e6'),
                                                            boxShadow: isSelected ? '0 0 0 1.5px #183eeb' : 'none',
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                        onClick={() => {
                                                            if (routeForm.is_multizona) {
                                                                toggleZoneMulti(z.id);
                                                            } else {
                                                                handleSelectZoneSingle(z);
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ flex: 1, paddingRight: '8px' }}>
                                                            <div className="d-flex align-items-center gap-1 mb-1">
                                                                <span className="fw-bold text-truncate" style={{ fontSize: '0.85rem', color: isDark ? '#ffffff' : '#010028', maxWidth: '160px' }}>
                                                                    {z.name}
                                                                </span>
                                                                <span className="badge rounded-pill bg-light text-muted border px-1" style={{ fontSize: '0.64rem' }}>
                                                                    {z.code}
                                                                </span>
                                                            </div>
                                                            <div style={{ fontSize: '0.74rem' }}>
                                                                {titularName ? (
                                                                    <span className="text-muted d-inline-flex align-items-center gap-1">
                                                                        <i className={routeForm.tipo_ruta === 'merchandising' ? "mdi mdi-store text-info" : "mdi mdi-account-tie text-primary"}></i>
                                                                        <strong style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>{titularName}</strong>
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted fst-italic" style={{ fontSize: '0.7rem' }}>
                                                                        Sin titular
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            {routeForm.is_multizona ? (
                                                                <Form.Check 
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => {}}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChangeCapture={() => toggleZoneMulti(z.id)}
                                                                    style={{ transform: 'scale(1.15)', cursor: 'pointer' }}
                                                                />
                                                            ) : (
                                                                <Form.Check 
                                                                    type="radio"
                                                                    name="single-zone-choice"
                                                                    checked={isSelected}
                                                                    onChange={() => {}}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChangeCapture={() => handleSelectZoneSingle(z)}
                                                                    style={{ transform: 'scale(1.15)', cursor: 'pointer' }}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                        {zones.length === 0 && (
                                            <Col xs={12}>
                                                <div className="text-center py-4 text-muted small">
                                                    No hay zonas registradas.
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            </Col>

                            {/* COLUMNA DERECHA: Ficha de la Ruta y Responsable */}
                            <Col lg={5}>
                                <div 
                                    className="p-3 rounded border h-100 d-flex flex-column justify-content-between"
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        borderColor: isDark ? '#2a3759' : '#e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    <div>
                                        <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2" style={{ borderColor: isDark ? '#233052' : '#f1f5f9' }}>
                                            <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.92rem' }}>
                                                <i className="mdi mdi-clipboard-text-outline text-primary me-1"></i> Ficha de la Ruta
                                            </h6>
                                            <Badge bg={routeForm.tipo_ruta === 'merchandising' ? 'info' : 'primary'} style={{ fontSize: '0.68rem' }}>
                                                {routeForm.tipo_ruta === 'merchandising' ? 'Trade Marketing' : 'Preventa'}
                                            </Badge>
                                        </div>

                                        {/* Selector Tipo de Operación */}
                                        <div className="mb-3">
                                            <Form.Label className="small fw-bold text-uppercase text-muted mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                                                Operación
                                            </Form.Label>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant={routeForm.tipo_ruta === 'preventa' ? 'primary' : 'outline-secondary'}
                                                    size="sm"
                                                    className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-1"
                                                    style={{ borderRadius: '8px', fontSize: '0.8rem', padding: '5px' }}
                                                    onClick={() => handleRouteTypeChange('preventa')}
                                                >
                                                    <i className="mdi mdi-cart-outline fs-6"></i> Preventa
                                                </Button>
                                                <Button
                                                    variant={routeForm.tipo_ruta === 'merchandising' ? 'info' : 'outline-secondary'}
                                                    size="sm"
                                                    className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-1"
                                                    style={{ borderRadius: '8px', fontSize: '0.8rem', padding: '5px' }}
                                                    onClick={() => handleRouteTypeChange('merchandising')}
                                                >
                                                    <i className="mdi mdi-store-search fs-6"></i> Mercaderista
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Responsable Asignado Card */}
                                        <div className="mb-3">
                                            <Form.Label className="small fw-bold text-uppercase text-muted mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                                                {routeForm.tipo_ruta === 'merchandising' ? 'Mercaderista Responsable' : 'Vendedor Responsable'}
                                            </Form.Label>

                                            {assignedUser ? (
                                                <div 
                                                    className="p-2 px-3 rounded border mb-2"
                                                    style={{ 
                                                        backgroundColor: isDark ? '#161e38' : '#ffffff',
                                                        borderColor: isDifferentFromTitular ? '#f59e0b' : (isDark ? '#233052' : '#e2e8f0')
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                            <span className="fw-bold" style={{ fontSize: '0.9rem', color: isDark ? '#ffffff' : '#010028' }}>
                                                                {assignedUser.nombre_completo}
                                                            </span>
                                                            {assignedUserStatus?.isFree ? (
                                                                <span className="badge badge-status-free px-2 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#ebfe52', color: '#000000', fontSize: '0.64rem', borderRadius: '4px' }}>
                                                                    <i className="mdi mdi-check-circle me-1" style={{ color: '#000000' }}></i> Libre
                                                                </span>
                                                            ) : isCurrentTitularAssigned ? (
                                                                <span className="badge badge-status-official px-2 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#183eeb', color: '#ffffff', fontSize: '0.64rem', borderRadius: '4px' }}>
                                                                    <i className="mdi mdi-shield-check me-1"></i> Titular
                                                                </span>
                                                            ) : (
                                                                <span className="badge badge-status-zone px-2 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#2e00a8', color: '#ffffff', fontSize: '0.64rem', borderRadius: '4px' }}>
                                                                    <i className="mdi mdi-account-switch me-1"></i> Suplente
                                                                </span>
                                                            )}
                                                        </div>

                                                        <button 
                                                            type="button" 
                                                            className="btn btn-sm btn-link p-0 text-decoration-none small fw-semibold"
                                                            style={{ fontSize: '0.75rem', color: isDark ? '#93c5fd' : '#183eeb' }}
                                                            onClick={() => setShowChangeVendor(!showChangeVendor)}
                                                        >
                                                            {showChangeVendor ? 'Ocultar ▴' : 'Cambiar ▾'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-2 rounded text-muted small text-center mb-2" style={{ backgroundColor: isDark ? '#161e38' : '#f8f9fa', border: '1px dashed #cbd5e1' }}>
                                                    Selecciona una zona a la izquierda para cargar a su titular.
                                                </div>
                                            )}

                                            {/* Selector Desplegable de Vendedores */}
                                            {(showChangeVendor || !assignedUser || hasNoTitular) && (
                                                <div 
                                                    className="p-2 rounded border mb-2" 
                                                    style={{ 
                                                        backgroundColor: isDark ? '#0f172a' : '#f8fafc', 
                                                        borderColor: isDark ? '#1e293b' : '#e2e8f0' 
                                                    }}
                                                >
                                                    <input type="hidden" name="vendedor_id" value={routeForm.vendedor_id} required />

                                                    {/* Filtros Rápidos (Chips) */}
                                                    <div className="d-flex gap-1 mb-2 flex-wrap">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm d-inline-flex align-items-center gap-1"
                                                            style={{ 
                                                                fontSize: '0.72rem', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '6px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: sellerStatusFilter === 'all' ? '#183eeb' : (isDark ? '#161e38' : '#ffffff'),
                                                                color: sellerStatusFilter === 'all' ? '#ffffff' : (isDark ? '#94a3b8' : '#475569'),
                                                                border: sellerStatusFilter === 'all' ? 'none' : (isDark ? '1px solid #2a3759' : '1px solid #e2e8f0'),
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            onClick={() => setSellerStatusFilter('all')}
                                                        >
                                                            Todos ({sellerCounts.all})
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm d-inline-flex align-items-center gap-1 ${sellerStatusFilter === 'free' ? 'btn-status-free' : ''}`}
                                                            style={{ 
                                                                fontSize: '0.72rem', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '6px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: sellerStatusFilter === 'free' ? '#ebfe52' : (isDark ? '#161e38' : '#ffffff'),
                                                                color: sellerStatusFilter === 'free' ? '#000000' : (isDark ? '#ebfe52' : '#010028'),
                                                                border: sellerStatusFilter === 'free' ? 'none' : '1px solid #ebfe52',
                                                                boxShadow: sellerStatusFilter === 'free' ? '0 2px 8px rgba(235, 254, 82, 0.4)' : 'none',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            onClick={() => setSellerStatusFilter('free')}
                                                        >
                                                            <i className="mdi mdi-check-circle" style={{ color: sellerStatusFilter === 'free' ? '#000000' : (isDark ? '#ebfe52' : '#010028') }}></i>
                                                            <span>Libres ({sellerCounts.free})</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm d-inline-flex align-items-center gap-1"
                                                            style={{ 
                                                                fontSize: '0.72rem', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '6px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: sellerStatusFilter === 'with_zone' ? '#2e00a8' : (isDark ? '#161e38' : '#ffffff'),
                                                                color: sellerStatusFilter === 'with_zone' ? '#ffffff' : (isDark ? '#c084fc' : '#2e00a8'),
                                                                border: sellerStatusFilter === 'with_zone' ? 'none' : '1px solid #2e00a8',
                                                                boxShadow: sellerStatusFilter === 'with_zone' ? '0 2px 8px rgba(46, 0, 168, 0.35)' : 'none',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            onClick={() => setSellerStatusFilter('with_zone')}
                                                        >
                                                            <i className="mdi mdi-map-marker"></i>
                                                            <span>Con Zona ({sellerCounts.with_zone})</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm d-inline-flex align-items-center gap-1"
                                                            style={{ 
                                                                fontSize: '0.72rem', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '6px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: sellerStatusFilter === 'with_route' ? '#ff3b3b' : (isDark ? '#161e38' : '#ffffff'),
                                                                color: sellerStatusFilter === 'with_route' ? '#ffffff' : (isDark ? '#fca5a5' : '#ff3b3b'),
                                                                border: sellerStatusFilter === 'with_route' ? 'none' : '1px solid #ff3b3b',
                                                                boxShadow: sellerStatusFilter === 'with_route' ? '0 2px 8px rgba(255, 59, 59, 0.35)' : 'none',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            onClick={() => setSellerStatusFilter('with_route')}
                                                        >
                                                            <i className="mdi mdi-truck-fast"></i>
                                                            <span>Con Ruta ({sellerCounts.with_route})</span>
                                                        </button>
                                                    </div>

                                                    {/* Lista de Vendedores */}
                                                    <div 
                                                        style={{ 
                                                            maxHeight: '180px', 
                                                            overflowY: 'auto', 
                                                            paddingRight: '2px' 
                                                        }}
                                                    >
                                                        {filteredEnrichedSellers.length === 0 ? (
                                                            <div className="text-center py-2 text-muted small">
                                                                No hay registros en este filtro.
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex flex-column gap-1">
                                                                {filteredEnrichedSellers.map(u => {
                                                                    const isSelected = String(routeForm.vendedor_id) === String(u.id);
                                                                    return (
                                                                        <div
                                                                            key={u.id}
                                                                            className="p-2 px-3 rounded border d-flex align-items-center justify-content-between cursor-pointer"
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                backgroundColor: isDark 
                                                                                    ? (isSelected ? '#1e293b' : '#161e38')
                                                                                    : (isSelected ? '#eff6ff' : '#ffffff'),
                                                                                borderColor: isSelected ? '#183eeb' : (isDark ? '#233052' : '#e2e8f0'),
                                                                                transition: 'all 0.15s ease'
                                                                            }}
                                                                            onClick={() => setRouteForm(prev => ({ ...prev, vendedor_id: u.id }))}
                                                                        >
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <span className="fw-bold" style={{ fontSize: '0.82rem', color: isDark ? '#ffffff' : '#010028' }}>
                                                                                    {u.nombre_completo}
                                                                                </span>
                                                                                {u.isFree && (
                                                                                    <span className="badge badge-status-free px-1 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#ebfe52', color: '#000000', fontSize: '0.62rem', borderRadius: '4px' }}>
                                                                                        Libre
                                                                                    </span>
                                                                                )}
                                                                                {u.isTitularOfCurrentZone && (
                                                                                    <span className="badge badge-status-official px-1 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#183eeb', color: '#ffffff', fontSize: '0.62rem', borderRadius: '4px' }}>
                                                                                        Titular
                                                                                    </span>
                                                                                )}
                                                                                {u.hasZone && !u.isTitularOfCurrentZone && (
                                                                                    <span className="badge badge-status-zone px-1 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#2e00a8', color: '#ffffff', fontSize: '0.62rem', borderRadius: '4px' }}>
                                                                                        {u.zoneCode || 'Con Zona'}
                                                                                    </span>
                                                                                )}
                                                                                {u.hasRoute && (
                                                                                    <span className="badge badge-status-route px-1 py-0 fw-bold text-uppercase" style={{ backgroundColor: '#ff3b3b', color: '#ffffff', fontSize: '0.62rem', borderRadius: '4px' }}>
                                                                                        Con Ruta
                                                                                    </span>
                                                                                )}
                                                                            </div>

                                                                            <div>
                                                                                {isSelected ? (
                                                                                    <span className="badge badge-status-official px-2 py-0 fw-bold" style={{ backgroundColor: '#183eeb', color: '#ffffff', fontSize: '0.68rem', borderRadius: '4px' }}>
                                                                                        <i className="mdi mdi-check"></i>
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>Elegir</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Restaurar Titular */}
                                                    {isDifferentFromTitular && titularInfo.titularUser && (
                                                        <div className="mt-2 pt-1 border-top text-end" style={{ borderColor: isDark ? '#1e293b' : '#e2e8f0' }}>
                                                            <Button 
                                                                variant="link" 
                                                                size="sm" 
                                                                className="p-0 text-decoration-none small text-muted"
                                                                style={{ fontSize: '0.72rem' }}
                                                                onClick={() => {
                                                                    setRouteForm(p => ({ ...p, vendedor_id: titularInfo.titularId }));
                                                                    setShowChangeVendor(false);
                                                                }}
                                                            >
                                                                <i className="mdi mdi-undo me-1"></i>
                                                                Restaurar titular ({titularInfo.titularUser.nombre_completo})
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Nombre de la Ruta */}
                                        <div className="mb-3">
                                            <Form.Label className="small fw-bold text-uppercase text-muted mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                                                Nombre de la Ruta <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control 
                                                placeholder={routeForm.tipo_ruta === 'merchandising' ? "Ej: Ruta Mercadeo - [Nombre Zona]" : "Ej: Ruta Preventa - [Nombre Zona]"}
                                                value={routeForm.nombre_ruta}
                                                onChange={(e) => setRouteForm({ ...routeForm, nombre_ruta: e.target.value })}
                                                className="customer-filter-control w-100 shadow-none"
                                                style={{ fontSize: '0.85rem' }}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fc', borderTop: isDark ? '1px solid #2a3759' : '1px solid #dee2e6' }}>
                        <Button 
                            variant="light" 
                            onClick={() => setShowModal(false)}
                            style={{
                                borderRadius: '8px',
                                fontWeight: '600',
                                backgroundColor: isDark ? '#233052' : '#f1f5f9',
                                color: isDark ? '#ffffff' : '#334155',
                                border: 'none'
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            type="submit" 
                            style={{ backgroundColor: '#183eeb', border: 'none', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold' }}
                            disabled={savingRoute || routeForm.zone_ids.length === 0 || !routeForm.vendedor_id}
                        >
                            {savingRoute ? <Spinner size="sm" /> : (routeForm.id ? 'Actualizar Ruta' : 'Crear Ruta')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal Confirmación de Publicación de Agenda Semanal [FE-01] */}
            <Modal show={showPublishModal} onHide={() => !publishing && setShowPublishModal(false)} centered size="md">
                <Modal.Header closeButton={!publishing} closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
                    <Modal.Title style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        <i className="mdi mdi-cloud-upload-outline me-2 text-primary"></i>
                        Confirmar Publicación de Agenda Semanal
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                    <p className="mb-3" style={{ fontSize: '0.92rem' }}>
                        Estás a punto de <strong>publicar de forma definitiva</strong> la planificación semanal de visitas para esta ruta.
                    </p>

                    {activeRoute && (
                        <div className="p-3 rounded mb-3" style={{ backgroundColor: isDark ? '#10172d' : '#f8fafd', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="small text-muted">Ruta Comercial:</span>
                                <span className="fw-bold">{activeRoute.nombre_ruta}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="small text-muted">Responsable Destinatario:</span>
                                <span className="fw-semibold text-primary">{(activeRoute.responsable || activeRoute.vendedor)?.nombre_completo || 'Vendedor'}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="small text-muted">Tipo de Operación:</span>
                                <Badge bg={activeRoute.tipo_ruta === 'merchandising' ? 'info' : 'primary'}>
                                    {activeRoute.tipo_ruta === 'merchandising' ? 'Trade Marketing' : 'Preventa'}
                                </Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pt-2 border-top" style={{ borderColor: isDark ? '#233052' : '#e2e8f0' }}>
                                <span className="fw-bold">Total Visitas Semanales:</span>
                                <span className="badge rounded-pill bg-primary fs-6 px-3">{totalScheduledVisits} clientes</span>
                            </div>
                        </div>
                    )}

                    {/* Breakdown by day */}
                    <div className="mb-3 p-3 rounded" style={{ backgroundColor: isDark ? '#0d1326' : '#f1f5f9' }}>
                        <div className="fw-bold mb-2 text-muted text-uppercase" style={{ fontSize: '0.72rem' }}>Desglose de Visitas por Día:</div>
                        <div className="d-flex justify-content-between flex-wrap gap-2 text-center">
                            {daysOfWeek.map(d => (
                                <div key={d.id} className="p-1 px-2 rounded shadow-sm" style={{ backgroundColor: isDark ? '#19254d' : '#ffffff', minWidth: '42px', border: isDark ? '1px solid #283761' : '1px solid #e2e8f0' }}>
                                    <div className="text-muted small" style={{ fontSize: '0.68rem' }}>{d.name.substring(0, 3)}</div>
                                    <div className="fw-bold text-primary">{schedules[d.id]?.length || 0}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="alert alert-info py-2 px-3 d-flex align-items-start gap-2 mb-0" style={{ fontSize: '0.82rem', borderRadius: '8px' }}>
                        <i className="mdi mdi-cellphone-wireless fs-5 text-info mt-1"></i>
                        <div>
                            <strong>Sincronización PUSH Masiva:</strong> Al confirmar, el sistema emitirá una notificación push automática al dispositivo del vendedor para que sincronice su nueva agenda de visitas inmediatamente.
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8fafd', borderColor: isDark ? '#233052' : '#e2e8f0' }}>
                    <Button variant="secondary" onClick={() => setShowPublishModal(false)} disabled={publishing} style={{ borderRadius: '8px' }}>
                        Cancelar
                    </Button>
                    <Button 
                        style={{ backgroundColor: '#183eeb', border: 'none', borderRadius: '8px', fontWeight: 'bold' }} 
                        onClick={handlePublishSchedule}
                        disabled={publishing || totalScheduledVisits === 0}
                    >
                        {publishing ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Publicando y Notificando...
                            </>
                        ) : (
                            <>
                                <i className="mdi mdi-cloud-upload-outline me-1"></i>
                                Confirmar y Publicar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RoutePlanner;
